// @ts-check
import { isB2CPage } from './page.js';
import { SITE_TYPE, BREAKPOINTS } from '../constants/index.js';

export function noop() {}

/**
 * @function getValueByNs
 * @methodOf utils module
 * @param {*} obj
 * @param {string} path
 * @param {*} defaultValue
 * @returns {*}
 * @description return value from object by string path or default value
 */
export function getValueByNs(obj, path, defaultValue = '') {
  const keys = path.split('.');

  keys.forEach((key) => {
    if (obj) {
      obj = obj[key];
    }
  });

  return obj || defaultValue;
}

/**
 * Convert numbers to a string in format {number}px.
 *
 * @param {...number} values Numbers to convert.
 * @returns {string} String in format {number}px.
 */
export function convertToPx(...values) {
  return values.map((val) => `${val}px`).join(' ');
}

/**
 * Check if Mac is PC platform.
 *
 * @returns {boolean}
 */
export function isMac() {
  return navigator.platform.toLowerCase().indexOf('mac') !== -1;
}

/**
 * Detect whether running in Safari.
 *
 * @returns {boolean}
 */
export function isSafari() {
  const agent = navigator.userAgent.toLowerCase();

  return agent.indexOf('safari') !== -1 && agent.indexOf('chrome') === -1;
}

/**
 * Detect whether running in MS Edge.
 *
 * @returns {boolean}
 */
export function isEdge() {
  return typeof navigator !== 'undefined' && /Edge\/\d./i.test(navigator.userAgent);
}

/**
 * Detect whether running in IE 11.
 *
 * @returns {boolean}
 */
export function isIE() {
  return typeof navigator !== 'undefined' && /Trident\/.*rv:11/.test(navigator.userAgent);
}

/**
 * Detect whether running in IE 11 or Edge.
 *
 * @returns {boolean}
 */
export function isEdgeOrIE() {
  return typeof navigator !== 'undefined' && (isIE() || isEdge());
}

/**
 * Detect whether running in Firefox.
 *
 * @returns {boolean}
 */
export function isFirefox() {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
}

/**
 * Check if HTML element is outside the DOM tree.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
export function isHidden(el) {
  return !el.offsetWidth && !el.offsetHeight;
}

/**
 * Check if PES desktop viewport is used.
 *
 * @returns {boolean}
 */
export function isDesktop() {
  return window.innerWidth >= BREAKPOINTS.desktop;
}

/**
 * Check if PES wide desktop viewport is used.
 *
 * @returns {boolean}
 */
export function isWideDesktop() {
  return isDesktop() && window.innerWidth >= BREAKPOINTS.wideDesktop;
}

/**
 * Check if PES mobile viewport is used.
 *
 * @returns {boolean}
 */
export function isMobile() {
  return !isDesktop();
}

/**
 * Check if value is undefined.
 *
 * @param {?any} value
 * @returns {boolean}
 */
export function isUndefined(value) {
  return typeof value === 'undefined';
}

/**
 * Check if value is defined.
 *
 * @param {?any} value
 * @returns {boolean}
 */
export function isDefined(value) {
  return !isUndefined(value);
}

/**
 * Naive check if `value` is a plain object.
 * Consider using `is-plain-object` (https://github.com/jonschlinkert/is-plain-object) if this function doesn't work.
 *
 * @param {object} value
 */
export function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Determine Site Type from current page.
 *
 * @returns {'b2c'| 'b2b'}
 */
export function getSiteType() {
  return isB2CPage() ? SITE_TYPE.b2c : SITE_TYPE.b2b;
}

/**
 * Clears DOM contents. This is the fastest way, see https://www.measurethat.net/Benchmarks/Show/34/0/innerhtml-vs-removechild#latest_results_block.
 *
 * @param {Element} parentNode Parent Node which contents should be cleared.
 */
export function clearContents(parentNode) {
  while (parentNode.firstChild) {
    parentNode.removeChild(parentNode.firstChild);
  }
}

/**
 * Parse string and return defaultValue param if string cannot be parsed.
 *
 * @param {string} str Value to parse.
 * @param {?any} defaultValue Value to return if string cannot be parsed.
 * @returns {object}
 */
export function parseJSONString(str, defaultValue) {
  try {
    return JSON.parse(str);
  } catch (e) {
    return defaultValue;
  }
}

/**
 * Escape HTML tags as HTML entities.
 *
 * @param {string} html
 * @returns {string}
 */
export function escapeHtml(html) {
  const txtElement = document.createElement('textarea');

  txtElement.textContent = html;
  return txtElement.innerHTML;
}

/**
 * Remove HTML tags from the string.
 * See https://stackoverflow.com/questions/822452/strip-html-from-text-javascript.
 *
 * @param {string} html
 */
export function stripHTML(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  return (doc.body.textContent || '').trim();
}

function includesTag(tag) {
  return this.querySelectorAll(tag).length;
}

/**
 * Get selected elements from string.
 *
 * @param {string} htmlString HTML string from which elements should be returned.
 * @param {...string} [elementsToGet] CSS selectors to select elements to be returned from htmlString.
 * @returns {DocumentFragment}
 */
export function getElementsFromString(htmlString, ...elementsToGet) {
  // Create fragment (HTML template approach is not supported in IE) using ContextualFragment
  const fragment = document.createRange().createContextualFragment(htmlString);

  if (elementsToGet.length && elementsToGet.some(includesTag.bind(fragment))) {
    const result = document.createDocumentFragment();

    elementsToGet.forEach((tagToRemove) => {
      // Append all selected nodes to the result fragment
      const elementsToGet = fragment.querySelectorAll(tagToRemove);

      [...elementsToGet].forEach((element) => {
        result.appendChild(element);
      });
    });

    return result;
  }

  return fragment;
}

/**
 * Remove selected elements from string.
 *
 * @param {string} htmlString HTML string from which elements should be removed.
 * @param {...string} elementsToRemove CSS selectors to select elements to be removed from htmlString.
 * @returns {string}
 */
export function removeElements(htmlString, ...elementsToRemove) {
  // Create fragment (HTML template approach is not supported in IE) using ContextualFragment
  const fragment = document.createRange().createContextualFragment(htmlString);

  if (elementsToRemove.length && elementsToRemove.some(includesTag.bind(fragment))) {
    elementsToRemove.forEach((tagToRemove) => {
      // Remove all selected tags
      const elementsToRemove = fragment.querySelectorAll(tagToRemove);

      [...elementsToRemove].forEach((element) => {
        element.parentNode.removeChild(element);
      });
    });

    // Create wrapper helper to convert fragment to string
    const result = document.createElement('div');

    const fragmentNode = fragment.cloneNode(true);

    result.appendChild(fragmentNode);

    return result.innerHTML;
  }

  return htmlString;
}

/**
 * WhichAnimatableEnd
 * returns the name of transitionend/animationend event for cross browser compatibility.
 *
 * @param {string} type Of the animatableEvent: 'transition' or 'animation'.
 * @returns {string} The transitionend/animationend event name.
 */
export function whichAnimatableEnd(type) {
  const el = document.createElement('fakeelement');
  const capitalType = type.charAt(0).toUpperCase() + type.substring(1);
  let animatableEvent;

  const animations = {
    [type]: `${type}end`,
    [`O${capitalType}`]: `o${capitalType}End`,
    [`Moz${capitalType}`]: `${type}end`,
    [`Webkit${capitalType}`]: `webkit${capitalType}End`,
    [`MS${capitalType}`]: `MS${capitalType}End`
  };

  const hasEventEnd = Object.keys(animations).some((item) => {
    if (el.style[item] !== undefined) {
      animatableEvent = animations[item];
      return true;
    }

    return false;
  });

  if (!hasEventEnd) {
    throw new Error(`${type}end is not supported in your web browser.`);
  }

  return animatableEvent;
}

export const isElemOverflowed = (elem) => Math.ceil(elem.getBoundingClientRect().width) < elem.scrollWidth;

/**
 * Does object have any properties.
 *
 * @param {object} object
 */
export const isEmpty = (object) => Object.getOwnPropertyNames(object).length === 0;

/**
 * Object.prototype.hasOwnProperty.call shortcut.
 *
 * @param {object} object
 * @param {string} property
 * @returns {boolean}
 */
export const hasOwnProperty = (object, property) => Object.prototype.hasOwnProperty.call(object, property);

/** @param {string} string */
export function lowerCaseFirstChar(string) {
  return string.charAt(0).toLowerCase() + string.substring(1);
}

/**
 * Normalizes value as array.
 *
 * @param {Array | *} value
 * @returns {Array}
 */
export function getArray(value) {
  if (isUndefined(value)) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

/**
 * Check whether specified param is presented in URL query string.
 *
 * @param {string} paramName
 * @returns {boolean}
 */
export function hasSearchParam(paramName) {
  const { search } = window.location;

  if (!search) {
    return false;
  }

  const paramNames = search
    .slice(1)
    .split('&')
    .map((param) => param.split('=')[0]);

  return paramNames.includes(paramName);
}

/**
 * Get content value from HTML <meta> element.
 *
 * @param {string} nameValue
 * @returns {string}
 */
export const getContentFromMeta = (nameValue) => {
  const element = document.querySelector(`[name="${nameValue}"]`);

  return (element) ? element.getAttribute('content') : '';
};

/**
 * Checks whether its argument represents a numeric value.
 *
 * @param {?any} value To check.
 * @returns {boolean}
 */
export const isNumeric = (value) => {
  const parsedValue = parseFloat(value);

  return !Number.isNaN(parsedValue) && Number.isFinite(parsedValue);
};
